// Feature access — the platform's per-provider ALLOW-list (organization_feature_grants).
//
// The inverse of Service Controls: features listed here are OFF for every provider
// until master turns them on for that provider. The first is WhatsApp Embedded Signup
// config (user decision 14 Oct 2026): initially no provider may manage it; Green
// Giraffe, the platform root, holds every feature implicitly.
//
// MASTER_ADMIN only — the backend enforces MASTER_ADMIN/PLATFORM_ADMIN on
// /v1/feature-grants; the check here gives someone who types the URL a straight answer.
import React, { useEffect, useMemo, useState } from 'react';
import { KeyRound, Loader2, ShieldOff, Info } from 'lucide-react';
import { useAuth } from 'hooks/useAuth';
import {
  featureGrantsService, FeatureGrant, GrantedFeature, GRANTED_FEATURES,
} from 'api/services/featureGrants.service';
import { organizationsService, Organization } from 'api/services/organizations.service';
import { clearFeatureGrantsCache } from 'hooks/useFeatureGrants';
import { providerRows } from './featureAccessRules';

const FeatureAccess: React.FC = () => {
  const { user, currentOrganization } = useAuth();
  const role = ((currentOrganization as any)?.role || (user as any)?.role || '').toLowerCase();
  const isMasterAdmin = role === 'master_admin' || role === 'platform_admin';

  const [feature, setFeature] = useState<GrantedFeature>(GRANTED_FEATURES[0].key);
  const [grants, setGrants] = useState<FeatureGrant[]>([]);
  const [orgs, setOrgs] = useState<Organization[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState<string | null>(null);

  const load = async () => {
    setLoading(true);
    setError(null);
    const [g, services, businesses] = await Promise.all([
      featureGrantsService.list(),
      organizationsService.getServiceProviders().catch(() => ({ success: false, data: [] } as any)),
      // Only LIVE_CALL_SPY may be granted to a business; loaded once, shown for it alone.
      organizationsService.getAllBusinesses().catch(() => ({ success: false, data: [] } as any)),
    ]);
    if (g.success) setGrants(g.data || []);
    else setError(g.error || 'Failed to load feature grants');
    setOrgs([...(((services as any).data || []) as Organization[]), ...(((businesses as any).data || []) as Organization[])]);
    setLoading(false);
  };

  useEffect(() => { if (isMasterAdmin) load(); else setLoading(false); /* eslint-disable-next-line */ }, [isMasterAdmin]);

  const meta = GRANTED_FEATURES.find(f => f.key === feature)!;
  const rows = useMemo(() => providerRows(orgs, grants, feature, !!meta.businessGrantable),
    [orgs, grants, feature, meta.businessGrantable]);

  const toggle = async (orgId: string, name: string, granted: boolean) => {
    // eslint-disable-next-line no-restricted-globals
    if (!window.confirm(granted
      ? `Revoke "${meta.label}" from ${name}? Their admins lose the page immediately.`
      : `Grant "${meta.label}" to ${name}? Their admins will be able to use it for ${name}${meta.businessGrantable ? ' and its businesses' : ' only'}.`)) return;
    setBusy(orgId);
    setError(null);
    const r = granted
      ? await featureGrantsService.revoke(orgId, feature)
      : await featureGrantsService.grant(orgId, feature);
    setBusy(null);
    if (r.success) { clearFeatureGrantsCache(); load(); } else setError(r.error || 'Failed');
  };

  if (!isMasterAdmin) {
    return (
      <div className="tl-card text-center py-12">
        <ShieldOff className="w-8 h-8 mx-auto mb-3" style={{ color: 'var(--text-muted)' }} />
        <p className="text-sm font-medium text-gray-900 dark:text-white">Master admin only</p>
        <p className="text-[13px] mt-1 max-w-md mx-auto" style={{ color: 'var(--text-muted)' }}>
          Granting a provider control of platform configuration is a platform-level action.
        </p>
      </div>
    );
  }

  return (
    <div className="space-y-5">
      <div>
        <h1 className="text-2xl font-semibold tracking-tight text-gray-900 dark:text-white flex items-center gap-2">
          <KeyRound className="w-5 h-5" style={{ color: 'var(--accent)' }} />
          Feature access
        </h1>
        <p className="text-[13px] mt-1 max-w-2xl" style={{ color: 'var(--text-muted)' }}>
          Features here are off for every provider until you turn them on. A grant covers that organization only —
          not its businesses (Live call listening may also be granted to a business directly). The platform
          organization holds every feature.
        </p>
      </div>

      {GRANTED_FEATURES.length > 1 && (
        <select className="tl-input max-w-sm" value={feature} onChange={e => setFeature(e.target.value as GrantedFeature)}>
          {GRANTED_FEATURES.map(f => <option key={f.key} value={f.key}>{f.label}</option>)}
        </select>
      )}

      <div className="tl-card flex items-start gap-2 text-[13px]" style={{ color: 'var(--text-muted)' }}>
        <Info className="w-4 h-4 mt-0.5 shrink-0" />
        <span><strong className="text-gray-900 dark:text-white">{meta.label}.</strong> {meta.help}</span>
      </div>

      {error && <div className="bg-red-50 border border-red-200 text-red-700 text-sm rounded-xl px-3 py-2">{error}</div>}

      {loading ? (
        <div className="tl-card flex items-center gap-2 text-sm" style={{ color: 'var(--text-muted)' }}>
          <Loader2 className="w-4 h-4 animate-spin" /> Loading…
        </div>
      ) : rows.length === 0 ? (
        <div className="tl-card text-sm" style={{ color: 'var(--text-muted)' }}>No provider organizations found.</div>
      ) : (
        <div className="tl-card p-0 overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="text-left text-[12px]" style={{ color: 'var(--text-muted)' }}>
                <th className="px-4 py-2 font-medium">{meta.businessGrantable ? 'Organization' : 'Provider'}</th>
                <th className="px-4 py-2 font-medium">Access</th>
                <th className="px-4 py-2" />
              </tr>
            </thead>
            <tbody>
              {rows.map(r => (
                <tr key={r.id} className="border-t" style={{ borderColor: 'var(--border)' }}>
                  <td className="px-4 py-2 text-gray-900 dark:text-white">
                    {r.name}
                    {meta.businessGrantable && (
                      <span className="ml-2 text-[11px]" style={{ color: 'var(--text-muted)' }}>
                        {r.kind === 'SERVICE' ? 'provider' : 'business'}
                      </span>
                    )}
                  </td>
                  <td className="px-4 py-2">
                    {r.isRoot
                      ? <span className="tl-pill">Platform — always</span>
                      : r.granted
                        ? <span className="tl-pill tl-pill-amber">Granted</span>
                        : <span className="tl-pill">Not granted</span>}
                  </td>
                  <td className="px-4 py-2 text-right">
                    {!r.isRoot && (
                      <button
                        className={`tl-btn ${r.granted ? '' : 'tl-btn-primary'}`}
                        disabled={busy === r.id}
                        onClick={() => toggle(r.id, r.name, r.granted)}
                      >
                        {busy === r.id ? <Loader2 className="w-4 h-4 animate-spin" /> : r.granted ? 'Revoke' : 'Grant'}
                      </button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

export default FeatureAccess;
